import { useEffect, useState } from 'react';

/**
 * Cada cuánto se comprueba si hay una versión nueva publicada. Es una lectura
 * de `index.html` (menos de 1 KB, sin caché): cada minuto no pesa nada y quien
 * acaba de publicar no tiene que esperar cinco minutos para ver el aviso.
 */
const INTERVALO_MS = 60 * 1000;

/** Ruta del bundle que cargó esta pestaña, p. ej. `/assets/index.8c80adc6.js`. */
const bundleActual = (): string | null =>
  document
    .querySelector('script[type="module"][src*="/assets/"]')
    ?.getAttribute('src') ?? null;

/**
 * Avisa cuando se ha publicado una versión nueva de la aplicación.
 *
 * Cada despliegue genera un bundle con un nombre distinto, así que basta con
 * releer `index.html` (sin caché) y comparar a qué archivo apunta ahora. Si
 * cambió, es que hay versión nueva.
 *
 * No recarga por su cuenta: quien tenga una reserva a medio rellenar no debe
 * perderla. Solo devuelve `true` para que la interfaz ofrezca actualizar.
 */
export const useAppVersion = (): boolean => {
  const [hayVersionNueva, setHayVersionNueva] = useState(false);

  useEffect(() => {
    const actual = bundleActual();
    // En desarrollo no hay bundle con hash: no hay nada que comparar.
    if (!actual) return;

    let cancelado = false;

    const comprobar = async () => {
      if (cancelado || hayVersionNueva) return;
      try {
        const respuesta = await fetch(`/index.html?_=${Date.now()}`, {
          cache: 'no-store',
        });
        if (!respuesta.ok) return;

        const html = await respuesta.text();
        const encontrado = html.match(/src="(\/assets\/index\.[A-Za-z0-9]+\.js)"/);

        if (encontrado && encontrado[1] !== actual && !cancelado) {
          setHayVersionNueva(true);
        }
      } catch {
        /* sin conexión: se vuelve a intentar en la siguiente vuelta */
      }
    };

    const temporizador = setInterval(comprobar, INTERVALO_MS);
    // También al volver a la pestaña, que es cuando más se nota. Cambiar de
    // pestaña no siempre dispara `focus` en la ventana (pasó: se publicó, se
    // volvió a la pestaña y no salió el aviso), así que se escucha además
    // `visibilitychange`.
    const alVolver = () => {
      if (document.visibilityState === 'visible') void comprobar();
    };
    window.addEventListener('focus', comprobar);
    document.addEventListener('visibilitychange', alVolver);

    return () => {
      cancelado = true;
      clearInterval(temporizador);
      window.removeEventListener('focus', comprobar);
      document.removeEventListener('visibilitychange', alVolver);
    };
  }, [hayVersionNueva]);

  return hayVersionNueva;
};

export default useAppVersion;
